import { createSlice, createSelector } from '@reduxjs/toolkit';
import Constants from '../../utils/constants';
import { filterRowsByChecklist, defaultChecklistRange, normalizeStatut } from './conteneurMap.helpers';

/**
 * Libellé du statut de lavage à partir du code numérique renvoyé par l'API.
 * Aligné sur les constantes `CONTENEURS_STATUS_*_LIBELLE` (cf. constants.js).
 */
const getStatutLibelle = (status) => {
    if (status === null || status === undefined) return '';
    // eslint-disable-next-line eqeqeq
    if (status == Constants.CONTENEURS_STATUS_DONE) return Constants.CONTENEURS_STATUS_DONE_LIBELLE;
    // eslint-disable-next-line eqeqeq
    if (status == Constants.CONTENEURS_STATUS_TODO) return Constants.CONTENEURS_STATUS_TODO_LIBELLE;
    // eslint-disable-next-line eqeqeq
    if (status == Constants.CONTENEURS_STATUS_PROBLEME) return Constants.CONTENEURS_STATUS_PROBLEME_LIBELLE;
    // eslint-disable-next-line eqeqeq
    if (status == Constants.CONTENEURS_STATUS_INACCESSIBLE) return Constants.CONTENEURS_STATUS_INACCESSIBLE_LIBELLE;
    return '';
};

/**
 * État UI local de la vue carte conteneurs (Patrimoine v2).
 *
 * - `features` / `rows` ne sont PAS stockés ici : ils proviennent du cache RTK
 *   Query (`conteneurMapApi`). On lit le résultat avec `useGetConteneursGeoJsonQuery`
 *   et on dérive `rows` côté composant via les sélecteurs ci-dessous.
 * - Ce slice ne gère que l'état UI : filtre client-side, sélection courante,
 *   bbox carte, et toggle d'affichage des panneaux.
 */
const initialState = {
    selectedId: null,
    // 'lavage' (statut du dernier lavage) | 'checklist' (état du dernier acte checklist — PAT-04)
    mode: 'lavage',
    // Paramètres du mode checklist : acte choisi, plage (R6), filtres d'état / d'élément (R9), heatmap (R8)
    checklist: {
        acte: null,
        ...defaultChecklistRange(),
        etats: [],
        elements: [],
        heatmap: false,
    },
    filters: {
        search: '',
        patrimoineId: null,
        // R7 : sélection multi-patrimoines ; null = tous
        patrimoineIds: null,
        // Statuts de lavage retenus (codes int) ; vide = tous. Appliqué en mode Lavage uniquement.
        statuts: [],
        dechetTypes: [],
        conteneurTypes: [],
    },
    mapBounds: null,
    // 'both' | 'list' | 'map'
    panelMode: 'both',
};

const conteneurMapSlice = createSlice({
    name: 'conteneurMap',
    initialState,
    reducers: {
        setSelected: (state, action) => {
            state.selectedId = action.payload;
        },
        setSearch: (state, action) => {
            state.filters.search = action.payload ?? '';
        },
        setPatrimoineFilter: (state, action) => {
            state.filters.patrimoineId = action.payload ?? null;
        },
        setPatrimoineIdsFilter: (state, action) => {
            state.filters.patrimoineIds = Array.isArray(action.payload) ? action.payload : null;
        },
        setMode: (state, action) => {
            state.mode = action.payload === 'checklist' ? 'checklist' : 'lavage';
        },
        setChecklistActe: (state, action) => {
            state.checklist.acte = action.payload ?? null;
            state.checklist.elements = [];
        },
        setChecklistRange: (state, action) => {
            const { dateDebut, dateFin } = action.payload ?? {};
            if (dateDebut !== undefined) state.checklist.dateDebut = dateDebut;
            if (dateFin !== undefined) state.checklist.dateFin = dateFin;
        },
        setChecklistEtatsFilter: (state, action) => {
            state.checklist.etats = Array.isArray(action.payload) ? action.payload : [];
        },
        setChecklistElementsFilter: (state, action) => {
            state.checklist.elements = Array.isArray(action.payload) ? action.payload : [];
        },
        setChecklistHeatmap: (state, action) => {
            state.checklist.heatmap = !!action.payload;
        },
        setStatutsFilter: (state, action) => {
            state.filters.statuts = Array.isArray(action.payload) ? action.payload : [];
        },
        setDechetTypesFilter: (state, action) => {
            state.filters.dechetTypes = Array.isArray(action.payload) ? action.payload : [];
        },
        setConteneurTypesFilter: (state, action) => {
            state.filters.conteneurTypes = Array.isArray(action.payload) ? action.payload : [];
        },
        setMapBounds: (state, action) => {
            state.mapBounds = action.payload;
        },
        setPanelMode: (state, action) => {
            state.panelMode = action.payload;
        },
        resetConteneurMap: () => initialState,
        /** Restaure l'état persisté (cf. `pickPersistedState`) ; `null` → état initial. */
        hydrateConteneurMap: (_state, action) => {
            const saved = action.payload;
            if (!saved || typeof saved !== 'object') return initialState;
            return {
                ...initialState,
                mode: saved.mode === 'checklist' ? 'checklist' : 'lavage',
                panelMode: ['both', 'list', 'map'].includes(saved.panelMode) ? saved.panelMode : initialState.panelMode,
                checklist: { ...initialState.checklist, ...(saved.checklist ?? {}) },
                filters: { ...initialState.filters, ...(saved.filters ?? {}) },
            };
        },
    },
});

export const {
    setSelected,
    setSearch,
    setPatrimoineFilter,
    setPatrimoineIdsFilter,
    setMode,
    setChecklistActe,
    setChecklistRange,
    setChecklistEtatsFilter,
    setChecklistElementsFilter,
    setChecklistHeatmap,
    setStatutsFilter,
    setDechetTypesFilter,
    setConteneurTypesFilter,
    setMapBounds,
    setPanelMode,
    resetConteneurMap,
    hydrateConteneurMap,
} = conteneurMapSlice.actions;

export default conteneurMapSlice.reducer;

// --------- Sélecteurs ---------
// Les sélecteurs prennent `rows` en paramètre (pas dans le store) car les
// données viennent du cache RTK Query.

export const selectConteneurMapState = (state) => state.conteneurMap;
export const selectFilters = (state) => state.conteneurMap.filters;
export const selectSelectedId = (state) => state.conteneurMap.selectedId;
export const selectMapBounds = (state) => state.conteneurMap.mapBounds;
export const selectPanelMode = (state) => state.conteneurMap.panelMode;
export const selectMode = (state) => state.conteneurMap.mode;
export const selectChecklist = (state) => state.conteneurMap.checklist;

/** Sous-ensemble persisté en localStorage (filtres, mode, panneaux) — pas la sélection ni la bbox. */
export const pickPersistedState = (state) => ({
    mode: state.mode,
    panelMode: state.panelMode,
    checklist: state.checklist,
    filters: state.filters,
});

/**
 * Filtre les rows par texte libre (search).
 * Usage : `makeSelectFilteredRows()(rows, filters)`
 */
export const filterRowsBySearch = (rows, search) => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => {
        const statutLibelle = getStatutLibelle(r.status);
        return (
            (r.nomenclature && r.nomenclature.toLowerCase().includes(q)) ||
            (r.adresse && r.adresse.toLowerCase().includes(q)) ||
            (r.commune && r.commune.toLowerCase().includes(q)) ||
            (r.patrimoineLibelle && String(r.patrimoineLibelle).toLowerCase().includes(q)) ||
            (statutLibelle && statutLibelle.toLowerCase().includes(q))
        );
    });
};

/**
 * Filtre les rows par statuts de lavage (codes int). Tableau vide/null → pas de filtrage.
 * Un `status` null compte comme « Non lavé » (TODO).
 */
export const filterRowsByStatuts = (rows, statuts) => {
    if (!Array.isArray(statuts) || statuts.length === 0) return rows;
    const set = new Set(statuts.map(Number));
    return rows.filter((r) => set.has(normalizeStatut(r.status)));
};

/**
 * Filtre les rows par bbox courante.
 */
export const filterRowsByBounds = (rows, bounds) => {
    if (!bounds) return rows;
    return rows.filter((r) =>
        r.lat > bounds.south && r.lat < bounds.north &&
        r.lng > bounds.west && r.lng < bounds.east
    );
};

/**
 * Filtre les rows par patrimoineId. `null`/`undefined`/`''` → pas de filtrage.
 */
export const filterRowsByPatrimoine = (rows, patrimoineId) => {
    if (patrimoineId === null || patrimoineId === undefined || patrimoineId === '') return rows;
    // eslint-disable-next-line eqeqeq
    return rows.filter((r) => r.patrimoineId != null && String(r.patrimoineId) === String(patrimoineId));
};

/**
 * Filtre les rows par liste de patrimoineIds (R7). `null` → pas de filtrage ; `[]` → rien.
 */
export const filterRowsByPatrimoineIds = (rows, patrimoineIds) => {
    if (!Array.isArray(patrimoineIds)) return rows;
    const set = new Set(patrimoineIds.map((c) => String(c)));
    return rows.filter((r) => r.patrimoineId != null && set.has(String(r.patrimoineId)));
};

/**
 * Filtre les rows par liste de types de déchet (codes). Tableau vide/null → pas de filtrage.
 */
export const filterRowsByDechetTypes = (rows, dechetTypes) => {
    if (!Array.isArray(dechetTypes) || dechetTypes.length === 0) return rows;
    const set = new Set(dechetTypes.map((c) => String(c)));
    return rows.filter((r) => r.dechetType != null && set.has(String(r.dechetType)));
};

/**
 * Filtre les rows par liste de types de conteneur (codes). Tableau vide/null → pas de filtrage.
 */
export const filterRowsByConteneurTypes = (rows, conteneurTypes) => {
    if (!Array.isArray(conteneurTypes) || conteneurTypes.length === 0) return rows;
    const set = new Set(conteneurTypes.map((c) => String(c)));
    return rows.filter((r) => r.conteneurType != null && set.has(String(r.conteneurType)));
};

/**
 * Sélecteur composé memoizé : (rows, search, bounds, statuts, patrimoineId, ...) → { rows: capped, isOverCap }
 */
export const makeSelectCappedRows = () =>
    createSelector(
        [
            (rows) => rows,
            (_rows, search) => search,
            (_rows, _search, bounds) => bounds,
            (_rows, _search, _bounds, statuts) => statuts,
            (_rows, _search, _bounds, _statut, patrimoineId) => patrimoineId,
            (_rows, _search, _bounds, _statut, _patrimoineId, dechetTypes) => dechetTypes,
            (_rows, _search, _bounds, _statut, _patrimoineId, _dechetTypes, conteneurTypes) => conteneurTypes,
            (_rows, _search, _bounds, _statut, _patrimoineId, _dechetTypes, _conteneurTypes, patrimoineIds) => patrimoineIds,
            (_rows, _search, _bounds, _statut, _patrimoineId, _dechetTypes, _conteneurTypes, _patrimoineIds, checklistFilter) => checklistFilter,
        ],
        (rows, search, bounds, statuts, patrimoineId, dechetTypes, conteneurTypes, patrimoineIds, checklistFilter) => {
            const filteredByPatrimoine = filterRowsByPatrimoineIds(filterRowsByPatrimoine(rows, patrimoineId), patrimoineIds);
            const filteredByStatut = filterRowsByStatuts(filteredByPatrimoine, statuts);
            const filteredByDechet = filterRowsByDechetTypes(filteredByStatut, dechetTypes);
            const filteredByConteneur = filterRowsByConteneurTypes(filteredByDechet, conteneurTypes);
            const filteredByChecklist = checklistFilter
                ? filterRowsByChecklist(filteredByConteneur, checklistFilter.etats, checklistFilter.elements)
                : filteredByConteneur;
            const filtered = filterRowsBySearch(filteredByChecklist, search);
            const visible = filterRowsByBounds(filtered, bounds);
            // Pas de cap : la DataGrid est paginée + virtualisée, elle encaisse
            // sans souci l'ensemble des conteneurs visibles.
            return {
                rows: visible,
                isOverCap: false,
                totalFiltered: filtered.length,
                totalVisible: visible.length,
            };
        }
    );

/**
 * Sélecteur (rows, filters) → Set<number> des ids actifs après filtrage
 * search + patrimoineId + statuts, ou `null` si aucun filtre n'est actif.
 *
 * `null` signifie "tout afficher normalement" côté carte (pas de fantômes).
 */
export const makeSelectActiveIds = () =>
    createSelector(
        [
            (rows) => rows,
            (_rows, filters) => filters,
            (_rows, _filters, checklistFilter) => checklistFilter,
        ],
        (rows, filters, checklistFilter) => {
            const search = filters?.search ?? '';
            const patrimoineId = filters?.patrimoineId ?? null;
            const patrimoineIds = filters?.patrimoineIds ?? null;
            const statuts = filters?.statuts ?? [];
            const dechetTypes = filters?.dechetTypes ?? [];
            const conteneurTypes = filters?.conteneurTypes ?? [];
            const checklistEtats = checklistFilter?.etats ?? [];
            const checklistElements = checklistFilter?.elements ?? [];
            const hasFilter =
                (search && search.length > 0) ||
                patrimoineId != null ||
                Array.isArray(patrimoineIds) ||
                (Array.isArray(statuts) && statuts.length > 0) ||
                (Array.isArray(dechetTypes) && dechetTypes.length > 0) ||
                (Array.isArray(conteneurTypes) && conteneurTypes.length > 0) ||
                checklistEtats.length > 0 || checklistElements.length > 0;
            if (!hasFilter) return null;
            let filtered = filterRowsByStatuts(rows, statuts);
            filtered = filterRowsByPatrimoineIds(filtered, patrimoineIds);
            filtered = filterRowsByDechetTypes(filtered, dechetTypes);
            filtered = filterRowsByConteneurTypes(filtered, conteneurTypes);
            if (checklistFilter) filtered = filterRowsByChecklist(filtered, checklistEtats, checklistElements);
            filtered = filterRowsBySearch(filtered, search);
            if (patrimoineId != null) {
                // eslint-disable-next-line eqeqeq
                filtered = filtered.filter((r) => r.patrimoineId != null && String(r.patrimoineId) === String(patrimoineId));
            }
            return new Set(filtered.map((r) => r.id));
        }
    );
